import { FC } from 'react';
import { Box, Flex, Image, Text } from '@qga/roo-ui/components';
import { tick } from '@qga/roo-ui/assets';
import { DetailsBooking } from 'types';
import * as track from 'lib/gaTracking/crossPageEvents';
import * as trackBookingDetails from 'lib/gaTracking/bookingDetailsPageEvents';
import { getBookingCancellationQuotePageLink } from 'lib/links';
import { useRoutableModal } from 'lib/hooks/useRoutableModal';
import { ShowOnLargeScreen } from 'components/Layout';
import { Link, WideLink } from 'components/Typography';
import { FormattedDate } from 'components/Formatters';
import CancellationPolicyModal from 'components/Modals/CancellationPolicyModal';

const getCancellationPolicyTitle = (booking: DetailsBooking) => {
  if (!booking.reservation.cancellationPolicy?.activeCancellationWindow?.isNonRefundable) {
    return 'Your booking has a flexible cancellation policy';
  } else {
    return 'Your booking is non-refundable';
  }
};

const getCancellationRefundLabel = (booking: DetailsBooking) => {
  if (booking.reservation.cancellationPolicy?.activeCancellationWindow?.isFullyRefundable) {
    return 'full refund';
  } else if (booking.reservation.cancellationPolicy?.activeCancellationWindow?.isPartiallyRefundable) {
    return 'partial refund';
  } else {
    return '';
  }
};

const CancellationInfo: FC<{ booking: DetailsBooking }> = ({ booking }) => {
  const { isNonRefundable, endTime = '' } = booking.reservation.cancellationPolicy?.activeCancellationWindow || {};
  const { RoutableModalWrapper, ...modal } = useRoutableModal('cancellation-policy', {
    onCloseIteraction: track.cancellationPolicyPopUpCloseButtonSelected,
  });

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" alignItems={['left', 'center']} flexDirection={['column', 'row']}>
        <Flex flexDirection={['column', 'row']}>
          <ShowOnLargeScreen>{!isNonRefundable && <Image src={tick} alt="Refundable cancellation" pr={4} mb={[2, 0]} />}</ShowOnLargeScreen>
          <Box pr={4}>
            <Text fontSize="lg" color={isNonRefundable ? 'grey.charcoal' : 'green'} fontWeight="bold" as="div">
              {getCancellationPolicyTitle(booking)}
            </Text>
            <Text fontSize="sm" color="emperor" as="div">
              {!isNonRefundable && (
                <>
                  Cancel before <FormattedDate date={endTime} /> for a {getCancellationRefundLabel(booking)}.{' '}
                </>
              )}
              <Link color="emperor" underline to={modal.path} onClick={track.cancellationPolicyPopUpLinkSelected}>
                {isNonRefundable ? 'View hotel cancellation policy' : 'Learn more'}
              </Link>
            </Text>
          </Box>
        </Flex>
        {booking.flags.isCancelableInApp && !booking.flags.isFinalising && (
          <WideLink
            to={getBookingCancellationQuotePageLink(booking.id)}
            onClick={() => {
              trackBookingDetails.cancellationOptionButton('Policy');
            }}
            variant="primary"
            mt={[4, 0]}
            mr={[0, 2]}
          >
            VIEW CANCELLATION OPTIONS
          </WideLink>
        )}
      </Flex>
      <RoutableModalWrapper>
        <CancellationPolicyModal {...modal.props} rawDescription={booking.reservation.cancellationPolicy?.description ?? ''} />
      </RoutableModalWrapper>
    </Box>
  );
};

export default CancellationInfo;
